import { useReducer, useEffect } from 'react'
import _get from 'lodash/get.js'
import { fetcher } from '../../productDetail/Helper.js'

export const productTypes = {
  MF: 'mutual-funds',
  ETF: 'exchange-traded-funds',
  IF: 'interval-funds',
}

const ACTIONS = {
  LOAD_DATA: 'LOAD_DATA',
  SORT_DATA: 'SORT_DATA',
}

export const getDataConfig = {
  [productTypes.MF]: {
    productName: 'Mutual Fund',
    group: 'retail',
    resKey: 'value.mutualFunds.morningStarFunds',
    defaultClassName: 'Institutional',
  },
  [productTypes.ETF]: {
    productName: 'Exchange Traded Fund (ETF)',
    group: 'etf',
    resKey: 'value.etfs.morningStarFunds',
    defaultClassName: 'Exchange Traded Fund (ETF)',
  },
}

const initState = {
  isLoading: false,
  classOptions: {},
  selectedClass: {
    [productTypes.MF]: [getDataConfig[productTypes.MF].defaultClassName],
    [productTypes.ETF]: [getDataConfig[productTypes.ETF].defaultClassName],
  },
  rowsData: [],
  selectedSort: 'fundName',
  selectedSortDirection: true,
  selectedSortType: 'string',
  selectedDisplayMorningstarGroup: false,
  selectedGroups: [],
}

const getMutualFundClassMetadata = async () => {
  const url = `/pnp.svc/productTotals?group=Retail`
  const data = (await fetcher(url))?.value || {}
  return data?.classTotals
    ?.map((i) => i?.className || '')
    ?.sort((a, b) => a.localeCompare(b))
}

const getProductData = async (product, className = null) => {
  const getConfig = getDataConfig[product]
  let url = `/pnp.svc/productSummary?group=${getConfig?.group}&dataType=4,5`
  if (className) {
    url = url + '&className=' + className
  }
  const data = _get(await fetcher(url), getConfig?.resKey, [])
  return data?.reduce(
    (p, n) => [
      ...p,
      {
        ...n,
        fundType: getDataConfig[product]?.productName,
        productType: product,
        fundClass: n?.fundClass || getDataConfig[product]?.defaultClassName,
      },
    ],
    []
  )
}

const getSelectedClassProductData = async (product, classes) => {
  const classesArray = classes?.map((itm) => getProductData(product, itm))
  const data = await Promise.all(classesArray)
  return data.reduce((p, n) => [...p, ...n], [])
}

function combineReducer(state, action) {
  switch (action.type) {
    case ACTIONS.LOAD_DATA: {
      return {
        ...state,
        ...action.payload,
      }
    }
    case ACTIONS.SORT_DATA: {
      return {
        ...state,
        ...action.payload,
      }
    }
    default:
      return state
  }
}

export default function useCombineMorningstarfundsReducer() {
  const [state, dispatch] = useReducer(combineReducer, initState)
  const { selectedClass, selectedSort, selectedSortDirection, selectedGroups } =
    state

  const initialLoad = async () => {
    dispatch({
      type: ACTIONS.LOAD_DATA,
      payload: { isLoading: true },
    })

    const classesData = await getMutualFundClassMetadata()
    const productDataMF = await getSelectedClassProductData(
      productTypes.MF,
      selectedClass[productTypes.MF]
    )
    const productDataETF = await getSelectedClassProductData(
      productTypes.ETF,
      selectedClass[productTypes.ETF]
    )

    const payload = {
      isLoading: false,
      rowsData: [...productDataMF, ...productDataETF],
      classOptions: {
        [productTypes.MF]: classesData,
        [productTypes.ETF]: ['Exchange Traded Fund (ETF)'],
      },
    }
    dispatch({
      type: ACTIONS.LOAD_DATA,
      payload,
    })
  }

  const sortData = (payload = {}) => {
    dispatch({ type: ACTIONS.SORT_DATA, payload })
  }

  const onClickToggleGroup = (event) => {
    const group = event.currentTarget.dataset.group
    const indexOfEntry = selectedGroups.indexOf(group)
    const add = selectedGroups.indexOf(group) < 0
    const _array = [...selectedGroups]
    if (indexOfEntry > -1) {
      if (!add) {
        _array.splice(indexOfEntry, 1)
      }
    } else {
      if (add) {
        _array.push(group)
      }
    }
    sortData({ selectedGroups: _array })
  }

  const onClickSort = (event) => {
    const sort = event.currentTarget.dataset.sort
    const sortType = event.currentTarget.dataset.sortType
    const sortDirection = !!parseInt(event.currentTarget.dataset.sortDirection)
    sortData({ selectedSortType: sortType })
    if (sort === selectedSort) {
      sortData({ selectedSortDirection: !selectedSortDirection })
    } else {
      sortData({
        selectedSort: sort,
        selectedSortDirection: sortDirection,
      })
    }
  }

  useEffect(() => {
    initialLoad()
  }, [])

  return {
    ...state,
    onClickSort,
    sortData,
    onClickToggleGroup,
    initialLoad,
  }
}
